import * as Ably from 'ably'
import { nanoid } from 'nanoid'
import { http, parner } from './http'
//@ts-ignore
import * as changeKeys from 'change-case/keys'

interface IGetRtTokenData {
  capability: string
  clientId: string
  expires: number
  issued: number
  token: string
}

interface IListenerProps {
  /**
   * The unique identifier for the subscription.
   * If the same `key` is used for multiple subscriptions, the previous subscription will be removed.
   * This allows for reusing the same `key` with different `params`.
   */
  key: string

  /**
   * A static, immutable property representing the channel, for example, "service:domainName".
   */
  channel: string

  /**
   * A dynamic property that can modify the `channel`, for example, "service:domainName:{id}".
   */
  params?: string

  /**
   * The name of the action that triggered the event, such as "insert" or "update".
   */
  event?: string

  filter?: string
}

interface IListener {
  id: string
  props: IListenerProps
  handle: Ably.messageCallback<Ably.InboundMessage>
}

export class RealtimeService {
  client?: Ably.Realtime
  listeners: IListener[] = []
  getToken() {
    return http.get<IGetRtTokenData>('user/rtToken')
  }
  connection() {
    // A second call used to replace the client and orphan the first one: its connection stayed
    // open (and, with presence, kept the user "present") for as long as the tab lived
    if (this.client) return

    // console.debug('Realtime connection...')
    this.client = new Ably.Realtime({
      authCallback: (tokenParams, callback) => {
        this.getToken()
          .then((response) => {
            callback(null, response.data.token)
            // console.debug('Realtime connected:', response.data)
          })
          .catch((error) => callback((error as Error)?.message || '', null))
      },
    })
  }

  /**
   * Closes the connection so that Ably drops the client from presence right away instead of
   * waiting for the connection to time out. Resolves when closed or after `timeoutMs`.
   */
  close(timeoutMs = 1000): Promise<void> {
    const client = this.client
    this.client = undefined
    this.listeners = []

    if (!client) return Promise.resolve()

    return new Promise((resolve) => {
      const timer = setTimeout(resolve, timeoutMs)
      client.connection.once('closed', () => {
        clearTimeout(timer)
        resolve()
      })
      client.close()
    })
  }

  getChannel(channelName: string, options?: { filter?: string }) {
    if (this.client) {
      if (options?.filter) {
        return this.client.channels.getDerived(
          `${parner}:${channelName}`,
          options
        )
      }

      return this.client.channels.get(`${parner}:${channelName}`)
    }
  }

  getListeners(query: IListenerProps) {
    return this.listeners.filter((listener) =>
      Object.entries(query).reduce((prev, [key, value]) => {
        if (!prev) return prev

        return listener.props[key as keyof IListenerProps] === value
      }, true)
    )
  }

  offOnly(query: Omit<IListenerProps, 'params'>) {
    const listeners = this.getListeners(query)

    listeners.forEach((listener) => {
      const { props, handle } = listener
      const channel = this.getChannel(
        `${props.channel}${props.params ? `:${props.params}` : ''}`
      )
      if (channel) {
        if (props.event) {
          channel?.unsubscribe(props.event, handle)
        } else {
          channel?.unsubscribe(handle)
        }
        const index = this.listeners.indexOf(listener)
        this.listeners.splice(index, 1)
      }
    })
  }
  only<Payload>(
    props: IListenerProps,
    handle: (paylad: Payload, message: Ably.InboundMessage) => void
  ) {
    const current = this.getChannel(
      `${props.channel}${props.params ? `:${props.params}` : ''}`,
      props?.filter ? { filter: props.filter } : undefined
    )

    if (current) {
      const query = { ...props }
      delete query.params
      delete query.filter

      this.offOnly(query)

      const listenerHandle: IListener['handle'] = (message) =>
        handle(
          changeKeys.camelCase(message.data as object, 20, {
            mergeAmbiguousCharacters: false,
          }) as Payload,
          message
        )

      this.listeners.push({
        id: nanoid(),
        props,
        handle: listenerHandle,
      })

      if (props.event) {
        current.subscribe(props.event, listenerHandle)
      } else {
        current.subscribe(listenerHandle)
      }
    }
  }
}

export const realtimeService = new RealtimeService()
